import { Severity } from '@gripp/shared-logic';
import { useTranslation } from 'react-i18next';
import { Platform, ScrollView, useWindowDimensions, View } from 'react-native';
import { themedStyleSheet } from '../../themes/themedStyleSheet';
import { Button } from '../crop-circle/Button';
import { severityToVariant, TypeIcon } from '../crop-circle/Components/Type';
import { spacing } from '../crop-circle/tokens';
import type { IssueFooterProps } from './issueFooter';

const options: { severity?: Severity; label: string }[] = [
  { label: 'issue.types.note' },
  { severity: Severity.Red, label: 'issue.types.urgent' },
  { severity: Severity.Yellow, label: 'issue.types.pending' },
  { severity: Severity.Green, label: 'issue.types.solved' },
];

export const IssueFooterChatPosting = ({
  severity,
  onSeverityChange,
  onPressIn,
}: NonNullable<IssueFooterProps['chatPosting']> & {
  onPressIn?: () => void;
}) => {
  const { t } = useTranslation();
  const { fontScale } = useWindowDimensions();
  // The fixed-height choices fit the baseline font; enlarged text needs its natural height.
  const largeText = Platform.OS !== 'web' && fontScale > 1;
  const buttonStyle = largeText ? styles.largeTextButton : undefined;
  const content = (
    <>
      {options.map((option) => {
        const selected =
          (severity ?? Severity.Purple) ===
          (option.severity ?? Severity.Purple);
        return (
          <Button
            key={option.label}
            style={buttonStyle}
            state={selected ? 'active' : 'default'}
            leftIcon={
              <TypeIcon
                variant={severityToVariant[option.severity ?? Severity.Purple]}
                size={16}
              />
            }
            accessibilityLabel={t('chat.composer.sendAsType', {
              type: t(option.label),
            })}
            accessibilityState={{ selected }}
            onPressIn={onPressIn}
            onPress={() => onSeverityChange(option.severity)}
            testID={`issue-footer-status-${option.severity ?? 'note'}`}
          >
            {t(option.label)}
          </Button>
        );
      })}
      {severity && severity !== Severity.Purple && (
        <Button
          style={buttonStyle}
          onPressIn={onPressIn}
          onPress={() => onSeverityChange(undefined)}
          accessibilityLabel={t('issue.footer.clearStatus')}
          testID="issue-footer-clear-status"
        >
          {t('issue.footer.clearStatus')}
        </Button>
      )}
    </>
  );
  return largeText ? (
    <ScrollView
      horizontal
      keyboardShouldPersistTaps="handled"
      style={styles.scroller}
      contentContainerStyle={styles.largeTextOptions}
      accessibilityLabel={t('chat.composer.sendAs')}
      testID="issue-footer-status-scroll"
    >
      {content}
    </ScrollView>
  ) : (
    <View style={styles.options} accessibilityLabel={t('chat.composer.sendAs')}>
      {content}
    </View>
  );
};

const styles = themedStyleSheet(() => ({
  scroller: { flexGrow: 0 },
  largeTextOptions: { flexDirection: 'row', gap: spacing.sm },
  largeTextButton: {
    height: 'auto',
    minHeight: 32,
    paddingVertical: spacing.xs,
  },
  options: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: spacing.sm,
  },
}));
